import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import './AuthCharacters.css';

const Pupil = ({
  size = 12,
  maxDistance = 5,
  pupilColor = '#2D2D2D',
  forceLookX,
  forceLookY,
}) => {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  let x = 0;
  let y = 0;
  if (forceLookX !== undefined && forceLookY !== undefined) {
    x = forceLookX;
    y = forceLookY;
  } else if (ref.current) {
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const dist = Math.min(Math.hypot(dx, dy), maxDistance);
    const ang = Math.atan2(dy, dx);
    x = Math.cos(ang) * dist;
    y = Math.sin(ang) * dist;
  }

  return (
    <div
      ref={ref}
      className="ac-pupil"
      style={{
        width: size,
        height: size,
        backgroundColor: pupilColor,
        transform: `translate(${x}px, ${y}px)`,
      }}
    />
  );
};

const EyeBall = ({
  size = 18,
  pupilSize = 7,
  maxDistance = 5,
  isBlinking = false,
  isWide = false,
  forceLookX,
  forceLookY,
}) => {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  let x = 0;
  let y = 0;
  if (forceLookX !== undefined && forceLookY !== undefined) {
    x = forceLookX;
    y = forceLookY;
  } else if (ref.current && !isBlinking) {
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const dist = Math.min(Math.hypot(dx, dy), maxDistance);
    const ang = Math.atan2(dy, dx);
    x = Math.cos(ang) * dist;
    y = Math.sin(ang) * dist;
  }

  const w = isWide ? size * 1.35 : size;
  const h = isBlinking ? 2 : isWide ? size * 1.35 : size;

  return (
    <div
      ref={ref}
      className="ac-eyeball"
      style={{ width: w, height: h }}
    >
      {!isBlinking && (
        <div
          className="ac-eyeball-pupil"
          style={{
            width: pupilSize,
            height: pupilSize,
            transform: `translate(${x}px, ${y}px)`,
          }}
        />
      )}
    </div>
  );
};

export const CharacterScene = ({
  isTyping = false,
  password = '',
  showPassword = false,
  loginFailed = false,
  entering = false,
  className = '',
}) => {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [purpleBlink, setPurpleBlink] = useState(false);
  const [blackBlink, setBlackBlink] = useState(false);
  const [orangeClosed, setOrangeClosed] = useState(false);
  const [lookEachOther, setLookEachOther] = useState(false);
  const [purplePeek, setPurplePeek] = useState(false);
  const purpleRef = useRef(null);
  const blackRef = useRef(null);
  const yellowRef = useRef(null);
  const orangeRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  useEffect(() => {
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setPurpleBlink(true);
        setTimeout(() => {
          setPurpleBlink(false);
          schedule();
        }, 150);
      }, Math.random() * 4000 + 3000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        setBlackBlink(true);
        setTimeout(() => {
          setBlackBlink(false);
          schedule();
        }, 150);
      }, Math.random() * 4000 + 3000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isTyping) {
      setLookEachOther(true);
      const t = setTimeout(() => setLookEachOther(false), 800);
      return () => clearTimeout(t);
    }
    setLookEachOther(false);
    return undefined;
  }, [isTyping]);

  useEffect(() => {
    if (password.length > 0 && showPassword) {
      let peekTimer;
      const schedule = () => {
        peekTimer = setTimeout(() => {
          setPurplePeek(true);
          setTimeout(() => {
            setPurplePeek(false);
            schedule();
          }, 800);
        }, Math.random() * 3000 + 2000);
      };
      schedule();
      return () => clearTimeout(peekTimer);
    }
    setPurplePeek(false);
    return undefined;
  }, [password, showPassword]);

  // On login failure, orange closes its eyes briefly then reopens.
  useEffect(() => {
    if (!loginFailed) {
      setOrangeClosed(false);
      return undefined;
    }
    setOrangeClosed(true);
    const t1 = setTimeout(() => setOrangeClosed(false), 700);
    const t2 = setTimeout(() => setOrangeClosed(true), 950);
    const t3 = setTimeout(() => setOrangeClosed(false), 1450);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [loginFailed]);

  const calcPos = (ref) => {
    if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 3;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const faceX = Math.max(-15, Math.min(15, dx / 20));
    const faceY = Math.max(-10, Math.min(10, dy / 30));
    const bodySkew = Math.max(-6, Math.min(6, -dx / 120));
    return { faceX, faceY, bodySkew };
  };

  const purplePos = calcPos(purpleRef);
  const blackPos = calcPos(blackRef);
  const yellowPos = calcPos(yellowRef);
  const orangePos = calcPos(orangeRef);

  const passwordHidden = password.length > 0 && !showPassword;
  const passwordRevealed = password.length > 0 && showPassword;
  const purpleEnlarged = isTyping || passwordHidden;

  const purpleSkew = passwordRevealed
    ? 0
    : purpleEnlarged
      ? purplePos.bodySkew - 12
      : purplePos.bodySkew;
  const purpleX = passwordRevealed ? 0 : purpleEnlarged ? 40 : 0;

  const blackSkew = passwordRevealed
    ? 0
    : lookEachOther
      ? blackPos.bodySkew * 1.5 + 10
      : isTyping || passwordHidden
        ? blackPos.bodySkew * 1.5
        : blackPos.bodySkew;
  const blackX = lookEachOther && !passwordRevealed ? 20 : 0;

  const orangeSkew = passwordRevealed ? 0 : orangePos.bodySkew;
  const yellowSkew = passwordRevealed ? 0 : yellowPos.bodySkew;

  // Each character has a directional entry per spec.
  const purpleEntry = entering
    ? { x: -360, y: 380, rotate: -45, opacity: 0, skewX: 0 }
    : false;
  const blackEntry = entering
    ? { x: 0, y: -560, rotate: -10, opacity: 0, skewX: 0 }
    : false;
  const orangeEntry = entering
    ? { x: -480, y: 0, scaleY: 1.25, scaleX: 0.85, rotate: -8, opacity: 0, skewX: 0 }
    : false;
  const yellowEntry = entering
    ? { x: 0, y: 360, rotate: 0, opacity: 0, skewX: 0 }
    : false;

  const idle = { duration: 0.6, ease: 'easeInOut' };

  // Spring for the "drop" + bouncy landing.
  const heavyDrop = (delay) => ({
    type: 'spring',
    stiffness: 220,
    damping: 13,
    mass: 1.1,
    delay,
  });
  const slideIn = (delay) => ({
    type: 'spring',
    stiffness: 170,
    damping: 14,
    mass: 0.95,
    delay,
  });
  const popUp = (delay) => ({
    type: 'spring',
    stiffness: 200,
    damping: 15,
    mass: 0.9,
    delay,
  });

  return (
    <div className={`characters-section ac-scene ${className}`}>
      <div className="ac-stage">
        {/* PURPLE — enters from bottom-left, rotating, lands with a bounce */}
        <motion.div
          ref={purpleRef}
          className="ac-char ac-purple"
          initial={purpleEntry}
          animate={{
            x: purpleX,
            y: 0,
            rotate: loginFailed ? [0, -14, 14, -11, 11, -7, 7, 0] : 0,
            opacity: 1,
            skewX: purpleSkew,
            height: purpleEnlarged ? 363 : 330,
          }}
          transition={{
            x: entering ? slideIn(0.15) : idle,
            y: entering ? slideIn(0.15) : idle,
            rotate: loginFailed
              ? { duration: 0.85, ease: 'easeInOut' }
              : entering
                ? slideIn(0.15)
                : idle,
            opacity: { duration: 0.35, delay: entering ? 0.15 : 0 },
            skewX: idle,
            height: idle,
          }}
        >
          <div
            className="ac-eyes ac-purple-eyes"
            style={{
              left: loginFailed
                ? 35
                : passwordRevealed
                  ? 20
                  : lookEachOther
                    ? 55
                    : 45 + purplePos.faceX,
              top: loginFailed
                ? 50
                : passwordRevealed
                  ? 35
                  : lookEachOther
                    ? 65
                    : 40 + purplePos.faceY,
            }}
          >
            <EyeBall
              size={18}
              pupilSize={7}
              maxDistance={5}
              isBlinking={purpleBlink}
              forceLookX={
                loginFailed
                  ? 0
                  : passwordRevealed
                    ? purplePeek
                      ? 4
                      : -4
                    : lookEachOther
                      ? 3
                      : undefined
              }
              forceLookY={
                loginFailed
                  ? 4
                  : passwordRevealed
                    ? purplePeek
                      ? 5
                      : -4
                    : lookEachOther
                      ? 4
                      : undefined
              }
            />
            <EyeBall
              size={18}
              pupilSize={7}
              maxDistance={5}
              isBlinking={purpleBlink}
              forceLookX={
                loginFailed
                  ? 0
                  : passwordRevealed
                    ? purplePeek
                      ? 4
                      : -4
                    : lookEachOther
                      ? 3
                      : undefined
              }
              forceLookY={
                loginFailed
                  ? 4
                  : passwordRevealed
                    ? purplePeek
                      ? 5
                      : -4
                    : lookEachOther
                      ? 4
                      : undefined
              }
            />
          </div>
          {loginFailed && <div className="ac-purple-mouth ac-mouth-frown" />}
        </motion.div>

        {/* BLACK — drops from top, tilted, gravity bounce + wide-eyed when failing */}
        <motion.div
          ref={blackRef}
          className="ac-char ac-black"
          initial={blackEntry}
          animate={{
            x: blackX,
            y: 0,
            rotate: 0,
            opacity: 1,
            skewX: blackSkew,
          }}
          transition={{
            x: entering ? heavyDrop(0.05) : idle,
            y: entering ? heavyDrop(0.05) : idle,
            rotate: entering ? heavyDrop(0.05) : idle,
            opacity: { duration: 0.3, delay: entering ? 0.05 : 0 },
            skewX: idle,
          }}
        >
          <div
            className="ac-eyes ac-black-eyes"
            style={{
              left: loginFailed
                ? 18
                : passwordRevealed
                  ? 10
                  : lookEachOther
                    ? 32
                    : 26 + blackPos.faceX,
              top: loginFailed
                ? 30
                : passwordRevealed
                  ? 28
                  : lookEachOther
                    ? 12
                    : 32 + blackPos.faceY,
            }}
          >
            <EyeBall
              size={16}
              pupilSize={6}
              maxDistance={4}
              isBlinking={blackBlink}
              isWide={loginFailed}
              forceLookX={
                loginFailed
                  ? 0
                  : passwordRevealed
                    ? -4
                    : lookEachOther
                      ? 0
                      : undefined
              }
              forceLookY={
                loginFailed
                  ? 0
                  : passwordRevealed
                    ? -4
                    : lookEachOther
                      ? -4
                      : undefined
              }
            />
            <EyeBall
              size={16}
              pupilSize={6}
              maxDistance={4}
              isBlinking={blackBlink}
              isWide={loginFailed}
              forceLookX={
                loginFailed
                  ? 0
                  : passwordRevealed
                    ? -4
                    : lookEachOther
                      ? 0
                      : undefined
              }
              forceLookY={
                loginFailed
                  ? 0
                  : passwordRevealed
                    ? -4
                    : lookEachOther
                      ? -4
                      : undefined
              }
            />
          </div>
        </motion.div>

        {/* ORANGE — slides in from the left with squash/stretch + closes eyes on fail */}
        <motion.div
          ref={orangeRef}
          className="ac-char ac-orange"
          initial={orangeEntry}
          animate={{
            x: 0,
            y: 0,
            rotate: 0,
            scaleX: entering ? [0.85, 1.08, 0.96, 1] : 1,
            scaleY: entering ? [1.25, 0.9, 1.04, 1] : 1,
            opacity: 1,
            skewX: orangeSkew,
          }}
          transition={{
            x: entering ? slideIn(0.3) : idle,
            y: entering ? slideIn(0.3) : idle,
            rotate: entering ? slideIn(0.3) : idle,
            scaleX: entering
              ? { duration: 0.7, delay: 0.3, times: [0, 0.4, 0.7, 1] }
              : idle,
            scaleY: entering
              ? { duration: 0.7, delay: 0.3, times: [0, 0.4, 0.7, 1] }
              : idle,
            opacity: { duration: 0.3, delay: entering ? 0.3 : 0 },
            skewX: idle,
          }}
          style={{ transformOrigin: 'bottom center' }}
        >
          <div
            className="ac-eyes ac-orange-eyes"
            style={{
              left: loginFailed ? 70 : passwordRevealed ? 50 : 82 + orangePos.faceX,
              top: loginFailed ? 95 : passwordRevealed ? 85 : 90 + orangePos.faceY,
            }}
          >
            {orangeClosed ? (
              <>
                <span className="ac-eyelash" />
                <span className="ac-eyelash" />
              </>
            ) : (
              <>
                <Pupil
                  size={12}
                  maxDistance={5}
                  pupilColor="#2D2D2D"
                  forceLookX={loginFailed ? -2 : passwordRevealed ? -5 : undefined}
                  forceLookY={loginFailed ? 2 : passwordRevealed ? -4 : undefined}
                />
                <Pupil
                  size={12}
                  maxDistance={5}
                  pupilColor="#2D2D2D"
                  forceLookX={loginFailed ? 2 : passwordRevealed ? -5 : undefined}
                  forceLookY={loginFailed ? 2 : passwordRevealed ? -4 : undefined}
                />
              </>
            )}
          </div>
          {loginFailed && !orangeClosed && (
            <div className="ac-orange-mouth ac-mouth-flat" />
          )}
        </motion.div>

        {/* YELLOW — rises from the bottom, flat expression on fail */}
        <motion.div
          ref={yellowRef}
          className="ac-char ac-yellow"
          initial={yellowEntry}
          animate={{
            x: 0,
            y: 0,
            rotate: 0,
            opacity: 1,
            skewX: yellowSkew,
          }}
          transition={{
            x: entering ? popUp(0.45) : idle,
            y: entering ? popUp(0.45) : idle,
            rotate: entering ? popUp(0.45) : idle,
            opacity: { duration: 0.3, delay: entering ? 0.45 : 0 },
            skewX: idle,
          }}
        >
          <div
            className="ac-eyes ac-yellow-eyes"
            style={{
              left: loginFailed ? 40 : passwordRevealed ? 20 : 52 + yellowPos.faceX,
              top: loginFailed ? 45 : passwordRevealed ? 35 : 40 + yellowPos.faceY,
            }}
          >
            <Pupil
              size={12}
              maxDistance={5}
              pupilColor="#2D2D2D"
              forceLookX={loginFailed ? 0 : passwordRevealed ? -5 : undefined}
              forceLookY={loginFailed ? 0 : passwordRevealed ? -4 : undefined}
            />
            <Pupil
              size={12}
              maxDistance={5}
              pupilColor="#2D2D2D"
              forceLookX={loginFailed ? 0 : passwordRevealed ? -5 : undefined}
              forceLookY={loginFailed ? 0 : passwordRevealed ? -4 : undefined}
            />
          </div>
          <div
            className={`ac-yellow-mouth ${loginFailed ? 'ac-mouth-flat' : ''}`}
            style={{
              left: loginFailed
                ? 30
                : passwordRevealed
                  ? 10
                  : 40 + yellowPos.faceX,
              top: loginFailed
                ? 105
                : passwordRevealed
                  ? 88
                  : 88 + yellowPos.faceY,
            }}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default CharacterScene;
